import React from "react";

import { Clock, Eye, FileUp, FileX2 } from "lucide-react";

import { FLOW, SPONSOR_STEP } from "./flowCopy";

// The sponsor's three steps, in the step bar's words (F-UF-F21).
const STEPS_LINE = `${SPONSOR_STEP.sponsor}, ${SPONSOR_STEP.qa} and ${SPONSOR_STEP.viewer}`;

/** What someone who only reads the published briefing sees before the first publish. */
export const NOT_PUBLISHED_LINE =
  "The briefing appears here once it's published.";

interface Props {
  /** Shown only to someone who can upload (holds AE on the account). */
  onUpload?: () => void;
  /**
   * Someone who sees only the published briefing (on the team without a review role,
   * or in the organization): "Not published yet" (P7 / UI tour item 12).
   */
  reader?: boolean;
  /** On the account team without the AE role, reading the briefing page. */
  team?: boolean;
}

const CARD =
  "rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center";

/**
 * The sponsor's layout with no plan behind it (never uploaded, or reset).
 * Briefing / Briefing Questions / View source would all be empty, so the page says what is
 * missing instead of showing three empty steps. Never who uploads (UI tour item 13):
 * the sponsor gets the one waiting message they see while a briefing is written.
 */
const NoPlanYet: React.FC<Props> = ({ onUpload, reader, team }) => {
  if (onUpload)
    return (
      <div className={CARD}>
        <FileX2 className="mx-auto mb-3 h-12 w-12 text-gray-300" aria-hidden />
        <h2 className="mb-1 text-lg font-semibold text-gray-700">
          No account plan yet
        </h2>
        <p className="mx-auto max-w-md text-sm text-gray-500">
          {`Upload the account plan to build ${STEPS_LINE} for this account.`}
        </p>
        <button
          type="button"
          onClick={onUpload}
          className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-[#974ae4] px-4 py-2 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          <FileUp className="h-4 w-4" aria-hidden /> Upload account plan
        </button>
      </div>
    );
  if (team)
    return (
      <div className={CARD}>
        <FileX2 className="mx-auto mb-3 h-12 w-12 text-gray-300" aria-hidden />
        <h2 className="mb-1 text-lg font-semibold text-gray-700">
          No account plan yet
        </h2>
        <p className="mx-auto max-w-md text-sm text-gray-500">
          {`${FLOW.waitingAeUpload} the plan.`}
        </p>
      </div>
    );
  if (reader)
    return (
      <div className={CARD} data-testid="not-published-yet">
        <Eye className="mx-auto mb-3 h-12 w-12 text-gray-300" aria-hidden />
        <h2 className="mb-1 text-lg font-semibold text-gray-700">
          {FLOW.notPublishedYet}
        </h2>
        <p className="mx-auto max-w-md text-sm text-gray-500">
          {NOT_PUBLISHED_LINE}
        </p>
      </div>
    );
  return (
    <div className={CARD} data-testid="sponsor-briefing-writing">
      <Clock className="mx-auto mb-3 h-12 w-12 text-gray-300" aria-hidden />
      <h2 className="mb-1 text-lg font-semibold text-gray-700">
        {FLOW.sponsorBriefingWriting}
      </h2>
      <p className="mx-auto max-w-md text-sm text-gray-500">
        {FLOW.sponsorBriefingWritingSub}
      </p>
    </div>
  );
};

export default NoPlanYet;
